/**
 * Terminal screen for a nurse whose employment has ended.
 *
 * Reached either from a 403 `ACCOUNT_INACTIVE` on any request or from the
 * session endpoint reporting `blocked`. It clears the stored session on mount,
 * so cached visits, chats, and patient details are gone from the device rather
 * than merely hidden behind a screen a back gesture could escape.
 */
import React, { useEffect } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { colors, fonts } from "@/lib/theme";

export function AccountEndedScreen({ reason }: { reason: "terminated" | "deactivated" }) {
    const { logout, dismissAccountEnded } = useAuth();

    useEffect(() => {
        // The session was already wiped by reportAccountEnded before this
        // screen mounted. Call logout anyway so the server-side refresh token
        // is revoked too, and so the path is identical whether we got here from
        // a 403 or from the session endpoint.
        void logout();
    }, [logout]);

    return (
        <SafeAreaView style={styles.safe}>
            <View style={styles.wrap}>
                <Text style={styles.title}>Your access has ended</Text>
                <Text style={styles.body}>
                    {reason === "terminated"
                        ? "This account is no longer active, so it can no longer be used to view visits or patient information."
                        : "This account has been deactivated, so it can no longer be used to view visits or patient information."}
                </Text>
                <Text style={styles.body}>
                    If you believe this is a mistake, please contact Task Health.
                </Text>
                <TouchableOpacity style={styles.button} onPress={dismissAccountEnded}>
                    <Text style={styles.buttonText}>Return to sign in</Text>
                </TouchableOpacity>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.gray50 },
    wrap: { flex: 1, justifyContent: "center", paddingHorizontal: 28, gap: 16 },
    title: { fontSize: 26, fontFamily: fonts.extraBold, color: colors.gray900 },
    body: { fontSize: 15, fontFamily: fonts.regular, color: colors.gray600, lineHeight: 22 },
    button: {
        marginTop: 16,
        backgroundColor: colors.teal400,
        borderRadius: 16,
        paddingVertical: 18,
        alignItems: "center"
    },
    buttonText: { fontSize: 16, fontFamily: fonts.bold, color: "#FFFFFF" }
});
